import { lerpColor } from '../../color';
import { calmAt, calmCenter, catenary, glowDot, grain, rgba, vignette, type Painter } from '../../art/paint';
import { hsl, type World } from '../../world';

const TAU = Math.PI * 2;

interface Tones {
  readonly bg: number;
  readonly ink: number;
  readonly far: number;
  readonly ceil: number;
  readonly rim: number;
  readonly held: number;
  readonly spot: number;
  readonly light: number;
  readonly dark: number;
}

interface Disc {
  readonly x: number;
  readonly y: number;
  readonly r: number;
}

interface Layout {
  readonly portrait: boolean;
  readonly pu: number;
  readonly hy: number;
  readonly sandTop: number;
  readonly calTop: number;
  readonly moon: Disc;
}

interface Palm {
  readonly bx: number;
  readonly by: number;
  readonly tx: number;
  readonly ty: number;
  readonly r: number;
  readonly bend: number;
}

type Spec = readonly [number, number, number, number];

const MARIA: readonly Spec[] = [
  [-0.32, -0.22, 0.22, 0],
  [0.24, 0.08, 0.27, 0],
  [-0.08, 0.44, 0.15, 0],
];
const WIDE_BLOOMS: readonly Spec[] = [
  [0.885, 0.3, 0.1, 0],
  [0.8, 0.23, 0.05, 1],
  [0.965, 0.4, 0.045, 2],
  [0.05, 0.62, 0.04, 1],
];
const TALL_BLOOMS: readonly Spec[] = [
  [0.84, 0.215, 0.1, 0],
  [0.94, 0.3, 0.045, 1],
  [0.6, 0.205, 0.04, 2],
  [0.1, 0.36, 0.045, 2],
  [0.42, 0.21, 0.04, 1],
];
const WIDE_BOATS: readonly Spec[] = [
  [0.06, 0.035, 1, 0],
  [0.15, 0.05, 1.2, 0],
  [0.2, 0.028, 0.8, 0],
  [0.97, 0.03, 0.8, 0],
];
const TALL_BOATS: readonly Spec[] = [
  [0.48, 0.02, 1, 0],
  [0.64, 0.04, 1.2, 0],
  [0.3, 0.03, 0.8, 0],
  [0.16, 0.05, 1, 0],
  [0.8, 0.025, 0.8, 0],
];
const FRONDS = [2.7, -3.05, -2.5, -1.95, -1.4, -0.85, -0.3, 0.4];

function layoutOf(p: Painter): Layout {
  const portrait = p.w / p.h < 0.8;
  const pu = portrait ? p.w * 0.36 : p.unit;
  const r = Math.max(portrait ? 20 : 24, p.unit * 0.085);
  const moon = portrait
    ? { x: Math.max(r * 2.4, p.w * 0.15), y: p.h * 0.235, r }
    : { x: Math.max(r * 2.4, Math.min(p.w * 0.13, p.cx - p.unit * 1.08)), y: p.h * 0.3, r };
  return {
    portrait,
    pu,
    hy: p.h * (portrait ? 0.74 : 0.62),
    sandTop: p.h * (portrait ? 0.835 : 0.82),
    calTop: p.h * (portrait ? 0.88 : 0.9),
    moon,
  };
}

function spans(p: Painter, y: number): Array<readonly [number, number]> {
  const dy = (y - p.cy + 0.08 * p.unit) / p.unit;
  if (Math.abs(dy) >= 1) return [[0, p.w]];
  const half = Math.sqrt(1 - dy * dy) * p.unit * 1.08;
  return [
    [0, Math.max(0, p.cx - half)],
    [Math.min(p.w, p.cx + half), p.w],
  ];
}

function free(p: Painter, l: Layout, x: number, y: number, r: number): boolean {
  if (x - r < 6 || x + r > p.w - 6 || y - r < p.h * 0.2) return false;
  if (Math.hypot(x - p.vanish.x, y - p.vanish.y) < 0.42 * p.unit + r) return false;
  if (Math.hypot(x - l.moon.x, y - l.moon.y) < l.moon.r * 1.6 + r) return false;
  return !calmAt(p, x, y, 1);
}

function sky(p: Painter, t: Tones, l: Layout): void {
  const { ctx } = p;
  ctx.fillStyle = rgba(t.bg);
  ctx.fillRect(0, 0, p.w, p.h);
  const g = ctx.createLinearGradient(0, 0, 0, l.hy);
  g.addColorStop(0, rgba(lerpColor(t.bg, t.ink, 0.45)));
  g.addColorStop(0.55, rgba(t.bg));
  g.addColorStop(1, rgba(lerpColor(t.far, t.ceil, 0.3)));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, p.w, l.hy);
}

function stars(p: Painter, t: Tones, l: Layout): void {
  const { ctx } = p;
  const n = Math.round(Math.min(90, Math.max(28, (p.w * l.hy) / 9000)));
  for (let i = 0; i < n; i++) {
    const x = p.rng.range(0, p.w);
    const y = p.rng.range(0, l.hy * 0.9);
    const r = p.rng.range(0.5, 1.3);
    const a = p.rng.range(0.15, 0.45);
    if (calmAt(p, x, y, 1) || Math.hypot(x - l.moon.x, y - l.moon.y) < l.moon.r * 2) continue;
    ctx.fillStyle = rgba(t.rim, a);
    ctx.beginPath();
    ctx.arc(x, y, r, 0, TAU);
    ctx.fill();
  }
}

function moon(p: Painter, t: Tones, l: Layout): void {
  const { ctx } = p;
  const { x, y, r } = l.moon;
  glowDot(ctx, x, y, r * 4.2, t.rim, 0.13);
  ctx.fillStyle = rgba(lerpColor(t.far, t.rim, 0.8), 0.92);
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TAU);
  ctx.fill();
  ctx.fillStyle = rgba(lerpColor(t.rim, t.ceil, 0.55), 0.35);
  for (const [dx, dy, s] of MARIA) {
    ctx.beginPath();
    ctx.arc(x + dx * r, y + dy * r, s * r, 0, TAU);
    ctx.fill();
  }
  ctx.strokeStyle = rgba(t.rim, 0.5);
  ctx.lineWidth = Math.max(1, r * 0.05);
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TAU);
  ctx.stroke();
}

function bloom(p: Painter, t: Tones, x: number, y: number, r: number, tint: number): void {
  const { ctx } = p;
  glowDot(ctx, x, y, r * 1.5, tint, 0.1);
  const n = p.rng.int(14, 19);
  const turn = p.rng.range(0, TAU);
  const tips: number[] = [];
  ctx.lineCap = 'round';
  ctx.lineWidth = Math.max(0.8, r * 0.025);
  ctx.strokeStyle = rgba(tint, 0.24);
  ctx.beginPath();
  for (let i = 0; i < n; i++) {
    const a = turn + (i / n) * TAU + p.rng.range(-0.08, 0.08);
    const len = r * p.rng.range(0.72, 1);
    const tx = x + Math.cos(a) * len;
    const ty = y + Math.sin(a) * len + len * 0.08;
    ctx.moveTo(x + Math.cos(a) * r * 0.12, y + Math.sin(a) * r * 0.12);
    ctx.quadraticCurveTo(x + Math.cos(a) * len * 0.6, y + Math.sin(a) * len * 0.6, tx, ty);
    tips.push(tx, ty);
  }
  ctx.stroke();
  ctx.fillStyle = rgba(lerpColor(tint, t.rim, 0.3), 0.4);
  for (let i = 0; i < tips.length; i += 2) {
    ctx.beginPath();
    ctx.arc(tips[i]!, tips[i + 1]!, Math.max(1, r * 0.04), 0, TAU);
    ctx.fill();
  }
  ctx.fillStyle = rgba(tint, 0.35);
  ctx.beginPath();
  ctx.arc(x, y, Math.max(1.2, r * 0.06), 0, TAU);
  ctx.fill();
}

function blooms(p: Painter, t: Tones, l: Layout): void {
  const tints = [t.held, t.spot, t.light];
  const base = l.portrait ? Math.max(p.unit, p.w * 0.7) : p.unit;
  let left = p.level >= 4 ? 3 : 1;
  for (const [fx, fy, fr, tint] of l.portrait ? TALL_BLOOMS : WIDE_BLOOMS) {
    if (left === 0) break;
    const x = p.w * fx;
    const y = p.h * fy;
    const r = base * fr;
    if (!free(p, l, x, y, r)) continue;
    bloom(p, t, x, y, r, tints[tint] ?? t.held);
    left--;
  }
}

function sea(p: Painter, t: Tones, l: Layout): void {
  const { ctx } = p;
  const g = ctx.createLinearGradient(0, l.hy, 0, l.sandTop);
  g.addColorStop(0, rgba(lerpColor(t.far, t.ceil, 0.18)));
  g.addColorStop(1, rgba(lerpColor(t.bg, t.ink, 0.25)));
  ctx.fillStyle = g;
  ctx.fillRect(0, l.hy, p.w, l.sandTop - l.hy);
  ctx.strokeStyle = rgba(t.rim, 0.14);
  ctx.lineWidth = 1;
  for (const [a, b] of spans(p, l.hy)) {
    if (b - a < 2) continue;
    ctx.beginPath();
    ctx.moveTo(a, l.hy);
    ctx.lineTo(b, l.hy);
    ctx.stroke();
  }
  const n = Math.round(p.w / 36);
  ctx.lineCap = 'round';
  for (let i = 0; i < n; i++) {
    const d = p.rng.float();
    const x = p.rng.range(0, p.w);
    const y = l.hy + 4 + (l.sandTop - l.hy - 8) * d * d;
    const len = l.pu * (0.015 + 0.06 * d) * p.rng.range(0.6, 1.2);
    const a = p.rng.range(0.06, 0.15);
    if (calmAt(p, x, y, 1)) continue;
    ctx.strokeStyle = rgba(t.rim, a);
    ctx.lineWidth = 0.8 + d;
    ctx.beginPath();
    ctx.moveTo(x - len, y);
    ctx.quadraticCurveTo(x, y - len * 0.18, x + len, y);
    ctx.stroke();
  }
}

function dome(x: number, c: number, r: number, h: number): number {
  const q = 1 - ((x - c) / r) ** 2;
  return q <= 0 ? 0 : h * q ** 0.45;
}

function morro(p: Painter, t: Tones, l: Layout): void {
  const { ctx } = p;
  const u = l.pu * (l.portrait ? 0.75 : 1);
  const c1 = l.portrait ? p.w * 0.22 : Math.max(u * 0.34, p.w * 0.1);
  const r1 = u * 0.3;
  const h1 = u * (l.portrait ? 0.3 : 0.4);
  const c2 = c1 + u * 0.27;
  const r2 = u * 0.17;
  const h2 = u * 0.16;
  const left = c1 - r1;
  const right = c2 + r2;
  const pts: number[] = [];
  for (let i = 0; i <= 48; i++) {
    const x = left + ((right - left) * i) / 48;
    const h = Math.max(dome(x, c1, r1, h1), dome(x, c2, r2, h2));
    pts.push(x, l.hy - h * (1 + p.rng.range(-0.015, 0.015)));
  }
  ctx.fillStyle = rgba(lerpColor(t.bg, t.ink, 0.38));
  ctx.beginPath();
  ctx.moveTo(left, l.hy + 1);
  for (let i = 0; i < pts.length; i += 2) ctx.lineTo(pts[i]!, pts[i + 1]!);
  ctx.lineTo(right, l.hy + 1);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = rgba(t.ceil, 0.55);
  ctx.lineWidth = 1.3;
  ctx.lineJoin = 'round';
  ctx.beginPath();
  for (let i = 0; i < pts.length; i += 2) {
    if (i === 0) ctx.moveTo(pts[i]!, pts[i + 1]!);
    else ctx.lineTo(pts[i]!, pts[i + 1]!);
  }
  ctx.stroke();
  ctx.strokeStyle = rgba(lerpColor(t.far, t.ceil, 0.5), 0.28);
  ctx.lineWidth = 1;
  ctx.lineCap = 'round';
  for (let i = 0; i < 6; i++) {
    const x = c1 + p.rng.range(-0.6, 0.5) * r1;
    const top = l.hy - dome(x, c1, r1, h1);
    const y = top + (l.hy - top) * p.rng.range(0.2, 0.7);
    const len = r1 * p.rng.range(0.15, 0.3);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.quadraticCurveTo(x + len * 0.5, y + len * 0.4, x + len * 0.2, y + len);
    ctx.stroke();
  }
}

function lights(p: Painter, t: Tones, l: Layout): void {
  const { ctx } = p;
  const tones = [t.held, t.light, lerpColor(t.held, t.rim, 0.5)];
  const n = Math.round(p.w / 32);
  for (let i = 0; i < n; i++) {
    const x = p.rng.range(4, p.w - 4);
    const y = l.hy + p.rng.range(-1.5, 3.5);
    const r = p.rng.range(0.7, 1.6);
    const a = p.rng.range(0.3, 0.6);
    const c = p.rng.pick(tones);
    const halo = p.rng.chance(0.35);
    if (calmAt(p, x, y, 1)) continue;
    if (halo) glowDot(ctx, x, y, r * 5, c, 0.12);
    ctx.fillStyle = rgba(c, a);
    ctx.beginPath();
    ctx.arc(x, y, r, 0, TAU);
    ctx.fill();
  }
}

function glitter(p: Painter, t: Tones, l: Layout): void {
  const { ctx } = p;
  const tone = lerpColor(t.rim, t.held, 0.25);
  const depth = l.sandTop - l.hy;
  ctx.lineCap = 'round';
  for (let i = 0; i < 18; i++) {
    const d = (i + p.rng.range(0.1, 0.9)) / 18;
    const y = l.hy + 3 + (depth - 6) * d ** 1.3;
    const hw = l.moon.r * (0.35 + 1.5 * d) * p.rng.range(0.6, 1.1);
    const x = l.moon.x + p.rng.range(-0.35, 0.35) * hw;
    const len = hw * p.rng.range(0.35, 0.9);
    const a = p.rng.range(0.16, 0.3);
    if (calmAt(p, x, y, 1)) continue;
    ctx.strokeStyle = rgba(tone, a);
    ctx.lineWidth = 1 + d;
    ctx.beginPath();
    ctx.moveTo(x - len / 2, y);
    ctx.lineTo(x + len / 2, y);
    ctx.stroke();
  }
}

function foam(p: Painter, t: Tones, l: Layout): void {
  const { ctx } = p;
  const wl = l.pu * p.rng.range(0.28, 0.36);
  const ph = p.rng.range(0, TAU);
  const amp = Math.max(1.5, l.pu * 0.006);
  ctx.lineCap = 'round';
  for (const [k, a] of [
    [0, 0.24],
    [1, 0.12],
  ] as const) {
    const y0 = l.sandTop - k * l.pu * 0.025;
    for (const [s0, s1] of spans(p, y0)) {
      if (s1 - s0 < 8) continue;
      ctx.strokeStyle = rgba(t.rim, a);
      ctx.lineWidth = 1.6 - k * 0.6;
      ctx.beginPath();
      for (let x = s0; x <= s1; x += 4) {
        const y = y0 + amp * Math.sin((x / wl) * TAU + ph + k * 1.7);
        if (x === s0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
    }
  }
}

function sand(p: Painter, t: Tones, l: Layout): void {
  const { ctx } = p;
  const g = ctx.createLinearGradient(0, l.sandTop, 0, l.calTop);
  g.addColorStop(0, rgba(lerpColor(t.bg, t.held, 0.1)));
  g.addColorStop(1, rgba(lerpColor(t.bg, t.held, 0.16)));
  ctx.fillStyle = g;
  ctx.fillRect(0, l.sandTop, p.w, l.calTop - l.sandTop);
}

function calcadao(p: Painter, t: Tones, l: Layout): void {
  const { ctx } = p;
  const top = l.calTop;
  const hh = p.h - top;
  ctx.fillStyle = rgba(t.far);
  ctx.fillRect(0, top, p.w, hh);
  ctx.strokeStyle = rgba(lerpColor(t.far, t.ceil, 0.5), 0.6);
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(0, top);
  ctx.lineTo(p.w, top);
  ctx.stroke();
  const n = l.portrait ? 2 : 3;
  const thick = hh / (n * 2 + 1);
  ctx.lineCap = 'butt';
  ctx.lineJoin = 'round';
  ctx.lineWidth = thick;
  for (let i = 0; i < n; i++) {
    const y0 = top + thick * (1.5 + i * 2);
    const amp = thick * p.rng.range(0.7, 1);
    const wl = l.pu * p.rng.range(0.26, 0.38);
    const ph = p.rng.range(0, TAU);
    const wl2 = wl * p.rng.range(2.3, 3.1);
    const ph2 = p.rng.range(0, TAU);
    ctx.strokeStyle = rgba(t.ceil, p.rng.range(0.2, 0.28));
    ctx.beginPath();
    for (let x = -12; x <= p.w + 12; x += 5) {
      const y = y0 + amp * Math.sin((x / wl) * TAU + ph) + amp * 0.3 * Math.sin((x / wl2) * TAU + ph2);
      if (x === -12) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
}

function quad(a: number, c: number, b: number, s: number): number {
  return (1 - s) * (1 - s) * a + 2 * (1 - s) * s * c + s * s * b;
}

function leaf(p: Painter, t: Tones, x: number, y: number, angle: number, len: number, fill: number): void {
  const { ctx } = p;
  const ex = x + Math.cos(angle) * len;
  const ey = y + Math.sin(angle) * len * 0.55 + len * 0.42;
  const qx = x + Math.cos(angle) * len * 0.55;
  const qy = y + Math.sin(angle) * len * 0.7 - len * 0.12;
  const steps = 16;
  const side: number[] = [];
  const other: number[] = [];
  for (let i = 0; i <= steps; i++) {
    const s = i / steps;
    const px = quad(x, qx, ex, s);
    const py = quad(y, qy, ey, s);
    const dx = 2 * (1 - s) * (qx - x) + 2 * s * (ex - qx);
    const dy = 2 * (1 - s) * (qy - y) + 2 * s * (ey - qy);
    const dl = Math.hypot(dx, dy) || 1;
    const w = len * 0.12 * Math.sin(Math.PI * s) ** 0.7 * (i % 2 === 0 ? 1 : 0.45);
    side.push(px - (dy / dl) * w, py + (dx / dl) * w);
    other.push(px + (dy / dl) * w, py - (dx / dl) * w);
  }
  ctx.fillStyle = rgba(fill);
  ctx.beginPath();
  ctx.moveTo(x, y);
  for (let i = 0; i < side.length; i += 2) ctx.lineTo(side[i]!, side[i + 1]!);
  for (let i = other.length - 2; i >= 0; i -= 2) ctx.lineTo(other[i]!, other[i + 1]!);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = rgba(t.ceil, 0.38);
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.quadraticCurveTo(qx, qy, ex, ey);
  ctx.stroke();
}

function palm(p: Painter, t: Tones, m: Palm): void {
  const { ctx } = p;
  const trunk = lerpColor(t.bg, t.ink, 0.62);
  const qx = (m.bx + m.tx) / 2 + m.bend;
  const qy = (m.by + m.ty) / 2;
  const wb = m.r * 0.17;
  const wt = m.r * 0.09;
  const side: number[] = [];
  const other: number[] = [];
  for (let i = 0; i <= 20; i++) {
    const s = i / 20;
    const px = quad(m.bx, qx, m.tx, s);
    const py = quad(m.by, qy, m.ty, s);
    const dx = 2 * (1 - s) * (qx - m.bx) + 2 * s * (m.tx - qx);
    const dy = 2 * (1 - s) * (qy - m.by) + 2 * s * (m.ty - qy);
    const dl = Math.hypot(dx, dy) || 1;
    const w = wb + (wt - wb) * s;
    side.push(px - (dy / dl) * w, py + (dx / dl) * w);
    other.push(px + (dy / dl) * w, py - (dx / dl) * w);
  }
  ctx.fillStyle = rgba(trunk);
  ctx.beginPath();
  for (let i = 0; i < side.length; i += 2) {
    if (i === 0) ctx.moveTo(side[i]!, side[i + 1]!);
    else ctx.lineTo(side[i]!, side[i + 1]!);
  }
  for (let i = other.length - 2; i >= 0; i -= 2) ctx.lineTo(other[i]!, other[i + 1]!);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = rgba(lerpColor(trunk, t.ceil, 0.5), 0.4);
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let i = 2; i < side.length - 2; i += 4) {
    ctx.moveTo(side[i]!, side[i + 1]!);
    ctx.lineTo(other[i]!, other[i + 1]!);
  }
  ctx.stroke();
  ctx.strokeStyle = rgba(t.ceil, 0.45);
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  for (let i = 0; i < other.length; i += 2) {
    if (i === 0) ctx.moveTo(other[i]!, other[i + 1]!);
    else ctx.lineTo(other[i]!, other[i + 1]!);
  }
  ctx.stroke();
  const frond = lerpColor(trunk, t.far, 0.3);
  for (const a of FRONDS) leaf(p, t, m.tx, m.ty, a + p.rng.range(-0.12, 0.12), m.r * p.rng.range(0.82, 1.05), frond);
  ctx.fillStyle = rgba(lerpColor(t.ink, t.held, 0.22));
  for (const [dx, dy] of [
    [-0.08, 0.1],
    [0.07, 0.11],
    [0, 0.17],
  ] as const) {
    ctx.beginPath();
    ctx.arc(m.tx + dx * m.r, m.ty + dy * m.r, m.r * 0.065, 0, TAU);
    ctx.fill();
  }
}

function palmsOf(p: Painter, l: Layout): Palm[] {
  if (l.portrait) {
    const r = l.pu * 0.3;
    return [
      { bx: p.w - r * 1.7, by: p.h * 0.99, tx: p.w - r * 1.3, ty: p.h * 0.76, r, bend: r * 0.2 },
      { bx: p.w - r * 2.8, by: p.h * 0.995, tx: p.w - r * 3.1, ty: p.h * 0.84, r: r * 0.75, bend: -r * 0.15 },
    ];
  }
  const r = l.pu * 0.24;
  return [
    { bx: p.w - r * 1.8, by: p.h * 0.97, tx: p.w - r * 1.3, ty: p.h * 0.5, r, bend: -l.pu * 0.03 },
    { bx: p.w - r * 1.75, by: p.h * 0.99, tx: p.w - r * 2.15, ty: p.h * 0.63, r: r * 0.78, bend: l.pu * 0.03 },
  ];
}

function kiosk(p: Painter, t: Tones, l: Layout, palms: readonly Palm[]): void {
  const { ctx } = p;
  const kw = l.pu * 0.2;
  const x = l.portrait ? Math.max(kw, p.w * 0.2) : p.w * 0.92 - kw * 0.1;
  const base = l.calTop - 1;
  const counter = base - kw * 0.42;
  const roof = base - kw * 0.82;
  const wood = lerpColor(t.bg, t.ink, 0.45);
  ctx.fillStyle = rgba(wood);
  ctx.fillRect(x - kw * 0.5, roof, kw * 0.07, base - roof);
  ctx.fillRect(x + kw * 0.43, roof, kw * 0.07, base - roof);
  glowDot(ctx, x, (roof + counter) / 2, kw * 0.9, t.held, 0.16);
  ctx.fillStyle = rgba(t.held, 0.42);
  ctx.fillRect(x - kw * 0.43, roof + kw * 0.06, kw * 0.86, counter - roof - kw * 0.06);
  ctx.fillStyle = rgba(wood);
  ctx.fillRect(x - kw * 0.55, counter, kw * 1.1, base - counter);
  ctx.strokeStyle = rgba(t.ceil, 0.45);
  ctx.lineWidth = 1;
  ctx.strokeRect(x - kw * 0.55, counter, kw * 1.1, base - counter);
  const straw = lerpColor(t.far, t.held, 0.14);
  ctx.fillStyle = rgba(straw);
  ctx.beginPath();
  ctx.moveTo(x - kw * 0.72, roof + kw * 0.04);
  ctx.quadraticCurveTo(x - kw * 0.3, roof - kw * 0.2, x, roof - kw * 0.42);
  ctx.quadraticCurveTo(x + kw * 0.3, roof - kw * 0.2, x + kw * 0.72, roof + kw * 0.04);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = rgba(lerpColor(straw, t.held, 0.25), 0.7);
  ctx.beginPath();
  for (let i = 0; i <= 14; i++) {
    const fx = x - kw * 0.7 + (kw * 1.4 * i) / 14;
    ctx.moveTo(fx, roof + kw * 0.03);
    ctx.lineTo(fx + p.rng.range(-1, 1), roof + kw * p.rng.range(0.09, 0.14));
  }
  ctx.stroke();
  const anchor = palms[l.portrait ? 1 : 0];
  if (anchor === undefined) return;
  const ax = quad(anchor.bx, (anchor.bx + anchor.tx) / 2 + anchor.bend, anchor.tx, 0.45);
  const ay = quad(anchor.by, (anchor.by + anchor.ty) / 2, anchor.ty, 0.45);
  const ex = l.portrait ? x + kw * 0.7 : x - kw * 0.7;
  const ey = roof + kw * 0.02;
  const sag = l.pu * 0.05;
  ctx.strokeStyle = rgba(lerpColor(t.ink, t.ceil, 0.5), 0.6);
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let i = 0; i <= 24; i++) {
    const c = catenary(ex, ey, ax, ay, sag, i / 24);
    if (i === 0) ctx.moveTo(c.x, c.y);
    else ctx.lineTo(c.x, c.y);
  }
  ctx.stroke();
  const bulbs = 7;
  const br = Math.max(1.6, l.pu * 0.008);
  for (let i = 1; i <= bulbs; i++) {
    const c = catenary(ex, ey, ax, ay, sag, i / (bulbs + 1));
    const tone = i % 2 === 0 ? t.spot : t.held;
    glowDot(ctx, c.x, c.y + br, br * 4, tone, 0.16);
    ctx.fillStyle = rgba(tone, 0.6);
    ctx.beginPath();
    ctx.arc(c.x, c.y + br, br, 0, TAU);
    ctx.fill();
  }
}

function boats(p: Painter, t: Tones, l: Layout): void {
  const { ctx } = p;
  const hull = lerpColor(t.bg, t.ink, 0.5);
  let left = 3;
  for (const [fx, fy, fs] of l.portrait ? TALL_BOATS : WIDE_BOATS) {
    if (left === 0) break;
    const s = l.pu * 0.032 * fs;
    const x = p.w * fx;
    const y = l.hy + p.h * fy * (l.sandTop - l.hy) / (p.h * 0.2);
    if (x - s < 4 || x + s > p.w - 4 || calmAt(p, x, y, 1)) continue;
    left--;
    ctx.fillStyle = rgba(hull);
    ctx.beginPath();
    ctx.moveTo(x - s, y);
    ctx.lineTo(x + s, y);
    ctx.lineTo(x + s * 0.7, y + s * 0.35);
    ctx.lineTo(x - s * 0.7, y + s * 0.35);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = rgba(t.ceil, 0.5);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x, y - s * 1.2);
    ctx.stroke();
    glowDot(ctx, x, y - s * 1.2, s * 1.6, t.held, 0.2);
    ctx.fillStyle = rgba(t.held, 0.55);
    ctx.beginPath();
    ctx.arc(x, y - s * 1.2, Math.max(1.2, s * 0.14), 0, TAU);
    ctx.fill();
  }
}

export function paint(p: Painter, look: World): void {
  const pal = look.palette;
  const t: Tones = {
    bg: pal.bg,
    ink: pal.ink,
    far: hsl(look.hue, 40, 16),
    ceil: look.ceil,
    rim: pal.rim,
    held: pal.held,
    spot: pal.spot,
    light: pal.incoming.circle,
    dark: lerpColor(pal.bg, pal.ink, 0.5),
  };
  const l = layoutOf(p);
  const palms = palmsOf(p, l);
  sky(p, t, l);
  stars(p, t, l);
  moon(p, t, l);
  blooms(p, t, l);
  sea(p, t, l);
  if (p.level >= 2) glitter(p, t, l);
  morro(p, t, l);
  lights(p, t, l);
  if (p.level >= 4) boats(p, t, l);
  sand(p, t, l);
  if (p.level >= 2) foam(p, t, l);
  calcadao(p, t, l);
  for (const m of [...palms].reverse()) palm(p, t, m);
  if (p.level >= 3) kiosk(p, t, l, palms);
  calmCenter(p, t.bg, 0.6, 1.1);
  grain(p, 0.05, true);
  vignette(p, t.ink, 0.45);
}
